/**
 * 角度配合参考实体定义工具。
 *
 * 文件职责：
 * 1. 为 Angle Mate 生成可序列化的自动参考轴；
 * 2. 识别由 Mate 自身派生的自动参考轴，避免重载恢复时误判为缺失拓扑；
 * 3. 不依赖 Viewer、Object3D、UI、文档或求解器实例。
 */

import {
  ASSEMBLY_MATE_SCHEMA_VERSION,
  MateEntityKind,
  type MateEntityRef,
} from './AssemblyMateDefinition';

/** 自动角度参考轴稳定引用前缀。 */
const AUTO_ANGLE_REFERENCE_STABLE_REF_PREFIX = 'assembly-mate:auto-angle-reference:';

/** 自动角度参考轴拓扑绑定 ID。 */
const AUTO_ANGLE_REFERENCE_BODY_BINDING_ID = 'assembly-mate:auto-angle-reference';

/** 自动角度参考轴组件 ID，表示该引用不随任一组件变换。 */
const AUTO_ANGLE_REFERENCE_COMPONENT_INSTANCE_ID = 'assembly-mate:auto-angle-reference';

/** Angle Reference Entity 垂直判断容差。 */
const ANGLE_REFERENCE_PERPENDICULAR_TOLERANCE = 1e-5;

/**
 * 判断有限数字。
 *
 * @param value 待判断值。
 * @returns 是否为有限数字。
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断三维点。
 *
 * @param value 待判断值。
 * @returns 是否为三维点。
 */
function isPointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => isFiniteNumber(item));
}

/**
 * 复制三维点。
 *
 * @param value 源三维点。
 * @returns 三维点副本。
 */
function clonePointTuple(value: TopologyPoint3D): TopologyPoint3D {
  return [value[0], value[1], value[2]];
}

/**
 * 从两个点计算方向。
 *
 * @param start 起点。
 * @param end 终点。
 * @returns 未归一化方向。
 */
function subtractPointTuple(start: TopologyPoint3D, end: TopologyPoint3D): TopologyPoint3D {
  return [
    end[0] - start[0],
    end[1] - start[1],
    end[2] - start[2],
  ];
}

/**
 * 计算叉积。
 *
 * @param first 第一向量。
 * @param second 第二向量。
 * @returns 叉积向量。
 */
function crossPointTuple(first: TopologyPoint3D, second: TopologyPoint3D): TopologyPoint3D {
  return [
    first[1] * second[2] - first[2] * second[1],
    first[2] * second[0] - first[0] * second[2],
    first[0] * second[1] - first[1] * second[0],
  ];
}

/**
 * 归一化三维向量。
 *
 * @param value 源向量。
 * @returns 单位向量；退化时返回 null。
 */
function normalizePointTuple(value: TopologyPoint3D): TopologyPoint3D | null {
  const length = Math.hypot(value[0], value[1], value[2]);
  if (!Number.isFinite(length) || length <= 1e-9) {
    return null;
  }
  return [
    value[0] / length,
    value[1] / length,
    value[2] / length,
  ];
}

/**
 * 从实体几何中读取方向。
 *
 * @param entity 配合实体。
 * @returns 世界空间方向；不支持时返回 null。
 */
function resolveAngleEntityDirection(entity: MateEntityRef): TopologyPoint3D | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;

  if (isPointTuple(geometry.normal)) {
    return normalizePointTuple(geometry.normal);
  }
  if (isPointTuple(geometry.axisDirection)) {
    return normalizePointTuple(geometry.axisDirection);
  }
  if (isPointTuple(geometry.direction)) {
    return normalizePointTuple(geometry.direction);
  }
  if (isPointTuple(geometry.start) && isPointTuple(geometry.end)) {
    return normalizePointTuple(subtractPointTuple(geometry.start, geometry.end));
  }

  return null;
}

/**
 * 从实体几何中读取参考轴原点。
 *
 * @param entity 配合实体。
 * @returns 世界空间点。
 */
function resolveAngleReferenceOrigin(entity: MateEntityRef): TopologyPoint3D {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  const point = [
    geometry.point,
    geometry.origin,
    geometry.axisOrigin,
    geometry.center,
    geometry.start,
  ].find(isPointTuple);

  return point ? clonePointTuple(point) : [0, 0, 0];
}

/**
 * 判断实体是否为自动角度参考轴。
 *
 * @param entity 配合实体。
 * @returns 是否为自动参考轴。
 */
export function isAssemblyMateAutoAngleReferenceEntity(entity: MateEntityRef | undefined): boolean {
  return Boolean(
    entity?.entityKind === MateEntityKind.ReferenceAxis
    && entity.stableRef.startsWith(AUTO_ANGLE_REFERENCE_STABLE_REF_PREFIX),
  );
}

/**
 * 判断实体能否作为 SOLIDWORKS Angle Mate 的 Reference Entity。
 *
 * 设计说明：
 * SOLIDWORKS 的 Angle Reference Entity 不是普通附加选择，而是用于定义有符号角度的旋转轴。
 * 参考实体必须能解析为轴向或法向，并且该方向需要同时垂直于两个角度配合实体的方向；
 * 否则后续拖动或配置切换时无法稳定区分正负角度分支。
 *
 * @param firstEntity 第一角度实体。
 * @param secondEntity 第二角度实体。
 * @param referenceEntity 候选参考实体。
 * @returns 是否可作为参考实体。
 */
export function canUseAssemblyMateAngleReferenceEntity(
  firstEntity: MateEntityRef,
  secondEntity: MateEntityRef,
  referenceEntity: MateEntityRef,
): boolean {
  const firstDirection = resolveAngleEntityDirection(firstEntity);
  const secondDirection = resolveAngleEntityDirection(secondEntity);
  const referenceDirection = resolveAngleEntityDirection(referenceEntity);
  if (!firstDirection || !secondDirection || !referenceDirection) {
    return false;
  }

  const firstDot = Math.abs(
    referenceDirection[0] * firstDirection[0]
    + referenceDirection[1] * firstDirection[1]
    + referenceDirection[2] * firstDirection[2],
  );
  const secondDot = Math.abs(
    referenceDirection[0] * secondDirection[0]
    + referenceDirection[1] * secondDirection[1]
    + referenceDirection[2] * secondDirection[2],
  );

  return firstDot <= ANGLE_REFERENCE_PERPENDICULAR_TOLERANCE
    && secondDot <= ANGLE_REFERENCE_PERPENDICULAR_TOLERANCE;
}

/**
 * 创建 Angle Mate 自动参考轴。
 *
 * 设计说明：
 * SOLIDWORKS 的 Auto Fill Reference Entity 会为角度配合寻找可定义有符号旋转的参考实体。
 * 手动 Reference Entity 已由控制器和 PropertyManager 支持；当用户未显式选择第三参考实体时，
 * 这里使用两选择实体初始方向的法向生成自动参考轴。该轴作为 Mate 定义的一部分持久化，
 * 避免后续拖动在镜像解之间跳变，且不绑定运行时 Object3D。
 *
 * @param firstEntity 第一选择实体。
 * @param secondEntity 第二选择实体。
 * @returns 自动参考轴；几何退化时返回 undefined。
 */
export function createAssemblyMateAutoAngleReferenceEntity(
  firstEntity: MateEntityRef,
  secondEntity: MateEntityRef,
): MateEntityRef | undefined {
  const firstDirection = resolveAngleEntityDirection(firstEntity);
  const secondDirection = resolveAngleEntityDirection(secondEntity);
  if (!firstDirection || !secondDirection) {
    return undefined;
  }

  const referenceDirection = normalizePointTuple(crossPointTuple(firstDirection, secondDirection));
  if (!referenceDirection) {
    return undefined;
  }

  const stableRef = `${AUTO_ANGLE_REFERENCE_STABLE_REF_PREFIX}${firstEntity.stableRef}:${secondEntity.stableRef}`;
  const origin = resolveAngleReferenceOrigin(firstEntity);
  const bodyObjectId = firstEntity.geometry.bodyObjectId
    ?? firstEntity.topologyRef?.bodyObjectId
    ?? AUTO_ANGLE_REFERENCE_BODY_BINDING_ID;
  const topologyRef: AssemblyTopologySelectionRef = {
    schemaVersion: ASSEMBLY_MATE_SCHEMA_VERSION,
    assemblyNodeId: firstEntity.assemblyNodeId,
    ...(firstEntity.partUuid ? { partUuid: firstEntity.partUuid } : {}),
    bodyBindingId: AUTO_ANGLE_REFERENCE_BODY_BINDING_ID,
    bodyObjectId,
    entityKind: 'edge',
    topologyIndex: -1,
    subEntityId: stableRef,
    persistentRef: stableRef,
  };
  const geometry: BRepGeometryDescriptor = {
    entityKind: 'referenceAxis',
    bodyObjectId,
    topologyIndex: -1,
    subEntityId: stableRef,
    persistentRef: stableRef,
    coordinateSpace: 'world',
    origin,
    direction: referenceDirection,
  };

  return {
    schemaVersion: ASSEMBLY_MATE_SCHEMA_VERSION,
    stableRef,
    assemblyNodeId: firstEntity.assemblyNodeId,
    componentInstanceId: AUTO_ANGLE_REFERENCE_COMPONENT_INSTANCE_ID,
    ...(firstEntity.partUuid ? { partUuid: firstEntity.partUuid } : {}),
    entityKind: MateEntityKind.ReferenceAxis,
    topologyRef,
    geometry,
    label: '参考轴',
  };
}
